import React, { useState } from 'react'
import GenericModal from '../../../../components/GenericModal'
import CsvMappingUpload from '../../../../components/CsvMappingUpload'
import { useAppDispatch } from '../../../../store'
import { SlotStatus, type ISlot } from '../../../../models/DistributionPlan'
import { getSlotSectionApi } from '../../../../store/slices/distributionPlanSlice/apis'
import { useMutation } from '@tanstack/react-query'
import Dropdown from '../../../../components/Dropdown'
import { CreateAndEditCourses } from '../CreateAndEditCourses'
import { notification } from '../../../../components/Notifications'
import { type ApplyFiltersAndDispatchParams } from '../../../../components/CustomTable/types'
import GenericButton from '../../../../components/GenericButton'
import { ButtonSize, ButtonType } from '../../../../components/GenericButton/styles'
import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import { grey } from '../../../../colors'
import { type ApiError } from '../../../../models/Common'
import { DistributionManager } from '../../../../APIManager'
import { Typography } from '@mui/material'
import { ResetTableButton } from '../../../../components/ResetTableButton'

interface CoursesTopbarActionsProps {
  slot: ISlot
  params?: ApplyFiltersAndDispatchParams
  isSlotSections?: boolean
}

const CoursesTopbarActions: React.FC<CoursesTopbarActionsProps> = ({ slot, params, isSlotSections }) => {
  const [isUploadModal, setIsUploadModal] = useState<boolean>(false)
  const [isCreateModal, setIsCreateModal] = useState<boolean>(false)
  const [isResetCourseSectionModal, setIsResetCourseSectionModal] = useState<boolean>(false)

  const dispatch = useAppDispatch()

  const closeUploadModal = (): void => {
    setIsUploadModal(false)
  }

  const uploadApi = (): void => {
    setIsUploadModal(true)
  }

  const addManually = (): void => {
    setIsCreateModal(true)
  }

  const menuItems = [
    { text: 'Upload Course Sections (.csv)', action: uploadApi },
    { text: 'Add Manually', action: addManually, hidden: slot.slotNodeDetails.slotStatus === SlotStatus.Closed }
  ]

  const { mutate: resetSections, isPending: isResetting } = useMutation({
    mutationFn: async () => {
      return await DistributionManager.resetCourseSections(slot?.slotId)
    },
    onSuccess: (response) => {
      notification(response?.data?.message, { variant: 'success' })
      window.location.reload()
      void dispatch(getSlotSectionApi({ urlId: slot?.slotId, params }))
    },
    onError: (error: ApiError) => {
      notification(error.message || 'Failed to reset course sections.', { variant: 'error' })
    }
  })

  const resetTooltip = 'This will remove all course sections from the current survey project. Use this action if you want to start over or re-import sections. This cannot be undone.'
  const resetModalText = 'Are you sure you want to reset all course sections for this survey project? This action will permanently remove all existing course sections and cannot be undone.'
  return (
    <>
      <Dropdown menuItems={menuItems} label='Add Course Sections' />
      <GenericModal
        isOpen={isCreateModal}
        title={'Add Course Section to Survey Project'}
        onClose={() => { setIsCreateModal(false) }}
        maxWidth='sm'
        action={false}
      >
        <CreateAndEditCourses handleCloseModal={() => { setIsCreateModal(false) }} slot={slot} />
      </GenericModal>

      {slot.slotNodeDetails.slotStatus === SlotStatus.Draft && (
        <ResetTableButton
          buttonLabel='Reset Course Sections'
          onConfirm={resetSections}
          resetTooltip={resetTooltip}
          resetModalText={resetModalText}
          isLoading={isResetting}
        />
      )}
      <CsvMappingUpload isOpen={isUploadModal} onClose={closeUploadModal} modalTitle="Course Sections" csvHeaderEndpoint="/api/slots/sections/csv_headers" uploadCsvEndpoint={`/api/slots/${slot?.slotId}/sections/`} onSuccess={() => {
        notification('Courses successfully uploaded', { variant: 'success' })
        void dispatch(getSlotSectionApi({ urlId: slot?.slotId, params }))
      }} />
    </>
  )
}

export default CoursesTopbarActions
